(function () {
  // Paddle & Padel — Judge.me runtime fixes.
  //
  // WHY THIS FILE EXISTS
  // There are THREE separate Judge.me widgets on this store (PDP review widget,
  // photo lightbox, homepage cards carousel). They use different class names for
  // the same things, and several are injected at runtime with inline styles,
  // which beat CSS !important. So instead of guessing selectors, everything here
  // identifies elements by what they ARE — computed colour, text shape, geometry.
  //
  // PERFORMANCE NOTES (this runs on paid traffic, including older Android):
  //   * ONE DOM walk per run, shared by every fix. getComputedStyle() is not a
  //     cheap read — it can force a style recalculation — so we call it at most
  //     twice per element per run and pass the result around.
  //   * The interval STOPS once the page settles. A MutationObserver handles
  //     everything after that.
  //   * The keyboard check is event-driven (focusin/focusout), not polled.

  var GOLD = '#E6C25A';
  var GREY = '#b0b0b0';
  var DARK = '#1a1a1a';
  var DARK_TEXT = '#141414';
  var LIGHT_TEXT = '#f2f0ff';

  // A close control is a small tap target. A lightbox BACKDROP also answers to
  // 'close' (clicking it dismisses the modal) but is the size of the screen —
  // styling that as a button painted a huge translucent black disc over the
  // whole lightbox. Never treat anything bigger than this as a close button.
  var CLOSE_MAX_PX = 80;

  var FG = null;
  function themeForeground() {
    if (FG) return FG;
    try {
      var v = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-foreground')
        .trim();
      FG = v ? 'rgb(' + v + ')' : 'rgb(242,240,255)';
    } catch (e) {
      FG = 'rgb(242,240,255)';
    }
    return FG;
  }

  // ---------------------------------------------------------------- helpers

  function cls(el) { return String(el.className || ''); }

  function attr(el, name) {
    try { return (el.getAttribute(name) || '').toLowerCase(); } catch (e) { return ''; }
  }

  function styleOf(el) {
    try { return getComputedStyle(el); } catch (e) { return null; }
  }

  function parseRgb(str) {
    if (!str) return null;
    var m = str.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?/);
    if (!m) return null;
    var a = m[4] === undefined ? 1 : parseFloat(m[4]);
    return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10), a];
  }

  function brightness(rgb) {
    return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  }

  function isGreenish(rgb) {
    if (!rgb) return false;
    var r = rgb[0], g = rgb[1], b = rgb[2];
    if (g < 60) return false;
    if (r > 170 && g > 170 && b > 170) return false;
    return (g - r) > 25;
  }

  function isLightPanel(rgb) {
    if (!rgb) return false;
    if (rgb[3] < 0.5) return false;
    return brightness(rgb) > 225;
  }

  function isStar(el) { return cls(el).indexOf('jdgm-star') !== -1; }

  // Does this element answer to 'close'? Class is the usual tell, but an
  // icon-only button often has no useful class and just aria-label="Close".
  // NOTE: this alone is NOT enough to style something as a close button — see
  // isCloseButton(), which also requires it to be small.
  function claimsClose(el) {
    if (cls(el).toLowerCase().indexOf('close') !== -1) return true;
    if (attr(el, 'aria-label').indexOf('close') !== -1) return true;
    if (attr(el, 'title').indexOf('close') !== -1) return true;
    if (attr(el, 'data-action').indexOf('close') !== -1) return true;
    return false;
  }

  function isCloseButton(el) {
    if (!claimsClose(el)) return false;
    var b = el.getBoundingClientRect();
    if (!b.width || !b.height) return false;
    return b.width <= CLOSE_MAX_PX && b.height <= CLOSE_MAX_PX;
  }

  // The white star pill on the PDP review cards is intentional — never repaint
  // it. Same for anything we injected ourselves (pp-*).
  function isProtected(el) {
    var c = cls(el);
    return isStar(el) || c.indexOf('rating') !== -1 || c.indexOf('pp-') !== -1;
  }

  function isMedia(el) {
    var t = el.tagName;
    return t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' ||
           t === 'IMG' || t === 'VIDEO';
  }

  function ownText(el) {
    var t = '';
    for (var i = 0; i < el.childNodes.length; i++) {
      var n = el.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue) t += n.nodeValue;
    }
    return t.trim();
  }

  function pseudoContent(el, which) {
    try {
      var c = getComputedStyle(el, which).content;
      return c && c !== 'none' ? c : '';
    } catch (e) { return ''; }
  }

  // !!! IMPORTANT !!!
  // Judge.me adds a class containing 'jdgm' to <body> while the review form is
  // open. A bare [class*="jdgm"] query therefore returns <body>, and walking its
  // descendants means walking the ENTIRE PAGE — which recoloured the whole nav
  // menu the moment someone clicked 'Write a review'. Never treat <body> or
  // <html> as a Judge.me container.
  function jdgmElements() {
    var out = [];
    var seen = [];
    var found = document.querySelectorAll('[class*="jdgm"]');
    for (var i = 0; i < found.length; i++) {
      var root = found[i];
      if (root === document.body || root === document.documentElement) continue;
      if (seen.indexOf(root) === -1) { seen.push(root); out.push(root); }
      var nodes = root.querySelectorAll('*');
      for (var j = 0; j < nodes.length; j++) out.push(nodes[j]);
    }
    return out;
  }

  function effectiveBg(el) {
    var node = el;
    while (node && node !== document.documentElement) {
      var cs = styleOf(node);
      if (!cs) return null;
      var rgb = parseRgb(cs.backgroundColor);
      if (rgb && rgb[3] >= 0.5) return rgb;
      node = node.parentElement;
    }
    return null;
  }

  // ------------------------------------------------------------ date/avatar
  var DATE_PATTERNS = [
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
    /^\d{4}-\d{1,2}-\d{1,2}$/,
    /^\d{1,2}\.\d{1,2}\.\d{2,4}$/,
    /^\d{1,2}\s+\w{3,9}\s+\d{4}$/i,
    /^\w{3,9}\s+\d{1,2},?\s+\d{4}$/i
  ];

  function looksLikeDate(text) {
    for (var i = 0; i < DATE_PATTERNS.length; i++) {
      if (DATE_PATTERNS[i].test(text)) return true;
    }
    return false;
  }

  function isRound(el, cs) {
    var r = cs.borderRadius || '';
    if (!r) return false;
    if (r.indexOf('%') !== -1) return parseFloat(r) >= 40;
    var px = parseFloat(r);
    var box = el.getBoundingClientRect();
    var min = Math.min(box.width, box.height);
    if (!min) return false;
    return px >= min * 0.4;
  }

  function findRoundAncestor(el) {
    var node = el;
    for (var i = 0; i < 4 && node; i++) {
      if (node === document.body) return null;
      var cs = styleOf(node);
      if (cs && isRound(node, cs)) return node;
      node = node.parentElement;
      if (node && cls(node).indexOf('jdgm') === -1 && i > 1) break;
    }
    return null;
  }

  // ------------------------------------------------------------- form stars
  var STAR_GLYPHS = /[\u2605\u2606\u22C6\u2729\u272A\u272B\u272C\u272D\u272E\u272F\u2730\u2B50]/;
  var EMPTY_GLYPHS = /[\u2606\u2729]/;

  function goldFormStars() {
    var forms = document.querySelectorAll('.jdgm-form, .jdgm-form-wrapper');
    if (!forms.length) return;
    for (var i = 0; i < forms.length; i++) {
      var nodes = forms[i].querySelectorAll('a, span, i');
      for (var j = 0; j < nodes.length; j++) {
        var el = nodes[j];
        if (isMedia(el) || claimsClose(el)) continue;

        var text = ownText(el);
        var emptyLink = el.tagName === 'A' && !text && !el.children.length;
        var marks = text;
        if (!emptyLink && !el.hasAttribute('data-score')) {
          marks += pseudoContent(el, ':before') + pseudoContent(el, ':after');
          if (!STAR_GLYPHS.test(marks)) continue;
        }

        var off = cls(el).indexOf('--off') !== -1 || EMPTY_GLYPHS.test(marks);
        el.style.setProperty('color', off ? GREY : GOLD, 'important');
        el.style.setProperty('text-decoration', 'none', 'important');

        var svgs = el.querySelectorAll('svg, svg *');
        for (var k = 0; k < svgs.length; k++) {
          svgs[k].style.setProperty('fill', off ? GREY : GOLD, 'important');
        }
      }
    }
  }

  // --------------------------------------------------------- iOS zoom guard
  function enforceInputSize() {
    var fields = document.querySelectorAll(
      '.jdgm-form input, .jdgm-form textarea, .jdgm-form select,' +
      '.jdgm-form-wrapper input, .jdgm-form-wrapper textarea, .jdgm-form-wrapper select'
    );
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var t = (el.getAttribute('type') || '').toLowerCase();
      if (t === 'hidden' || t === 'radio' || t === 'checkbox') continue;
      var cs = styleOf(el);
      if (cs && parseFloat(cs.fontSize) < 16) {
        el.style.setProperty('font-size', '16px', 'important');
      }
    }
  }

  // ==========================================================================
  // STICKY ATC vs THE ON-SCREEN KEYBOARD
  //
  // The sticky product bar only actually gets in the way when the keyboard is
  // up — it lands right on top of the field you're typing in. So that, and only
  // that, is the trigger. If the review form is open but the keyboard is down,
  // the bar shows normally. The CSS applies this at mobile widths only, so
  // desktop is untouched.
  //
  // Detection is by FOCUS: a focused text field inside the Judge.me form is what
  // actually raises the keyboard, and focus events fire reliably everywhere.
  // Viewport-height guessing is the fragile alternative — the viewport also
  // shrinks for unrelated reasons.
  // ==========================================================================
  function keyboardIsUp() {
    var ae = document.activeElement;
    if (!ae) return false;
    var t = ae.tagName;
    if (t !== 'INPUT' && t !== 'TEXTAREA') return false;
    var type = (ae.getAttribute('type') || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio' || type === 'hidden' ||
        type === 'submit' || type === 'button') return false;
    // Walk up rather than use closest() — older WebViews may not have it.
    var node = ae;
    while (node && node !== document.body) {
      if (cls(node).indexOf('jdgm-form') !== -1) return true;
      node = node.parentElement;
    }
    return false;
  }

  function syncKeyboardFlag() {
    if (document.body) document.body.classList.toggle('pp-kbd-open', keyboardIsUp());
  }

  // ------------------------------------------------------------------- main

  function run() {
    var els = jdgmElements();
    if (!els.length) { syncKeyboardFlag(); return; }

    var fg = themeForeground();

    // PASS 1 — backgrounds. Must complete before contrast is measured, since
    // repainting a panel changes what its text has to be readable against.
    //
    // NOTE: this deliberately does NOT require the element to sit in a
    // position:fixed overlay. An earlier version did — but the photo lightbox's
    // panel is positioned absolutely inside its container rather than fixed, so
    // the check returned false and left that panel white. Nothing inside a
    // Judge.me widget is legitimately white except the star pill and the form's
    // inputs, and both are excluded, so the plain colour test is safe.
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (isProtected(el) || isMedia(el)) continue;
      var cs = styleOf(el);
      if (!cs) continue;

      var bg = parseRgb(cs.backgroundColor);

      if (isGreenish(bg)) {
        el.style.setProperty('background-color', GOLD, 'important');
        el.style.setProperty('background-image', 'none', 'important');
        el.style.setProperty('color', '#0d0d0d', 'important');
        el.style.setProperty('border-color', GOLD, 'important');
        continue;
      }

      if (isLightPanel(bg)) {
        el.style.setProperty('background-color', DARK, 'important');
      }
    }

    // PASS 2 — foregrounds, close buttons, dates, avatars.
    for (var n = 0; n < els.length; n++) {
      var e = els[n];
      if (isStar(e)) continue;

      var s = styleOf(e);
      if (!s) continue;

      // Close BUTTON: make it obvious. Never hide it — it's the way out of the
      // modal. Size-gated: the backdrop also answers to 'close' and matching it
      // here painted a screen-sized translucent disc over the whole lightbox.
      if (isCloseButton(e)) {
        e.style.setProperty('color', '#ffffff', 'important');
        e.style.setProperty('opacity', '1', 'important');
        e.style.setProperty('background-color', 'rgba(0,0,0,0.55)', 'important');
        e.style.setProperty('border-radius', '50%', 'important');
        e.style.setProperty('z-index', '10', 'important');
        continue;
      }
      // Anything else claiming 'close' (i.e. the backdrop) is left alone.
      if (claimsClose(e)) continue;

      var colour = parseRgb(s.color);

      if (isGreenish(colour)) {
        e.style.setProperty('color', fg, 'important');
      }
      if (isGreenish(parseRgb(s.borderTopColor))) {
        e.style.setProperty('border-color', fg, 'important');
      }

      // SVG icons are coloured by FILL, not color/background — a different CSS
      // property entirely. That's why the green 'Review Submitted!' tick sailed
      // through the colour scrub untouched. Same for stroke-drawn icons.
      if (isGreenish(parseRgb(s.fill))) {
        e.style.setProperty('fill', GOLD, 'important');
      }
      if (isGreenish(parseRgb(s.stroke))) {
        e.style.setProperty('stroke', GOLD, 'important');
      }

      var text = ownText(e);
      if (!text) continue;

      if (looksLikeDate(text)) {
        e.style.setProperty('display', 'none', 'important');
        continue;
      }

      // Single-letter avatar bubble — hide the round container, not the letter.
      if (text.length === 1 && /^[A-Za-z]$/.test(text)) {
        var round = findRoundAncestor(e);
        if (round && !claimsClose(round)) {
          round.style.setProperty('display', 'none', 'important');
        }
        continue;
      }

      // Contrast repair.
      if (e.hasAttribute('data-score')) continue;
      var back = effectiveBg(e);
      if (!colour || !back) continue;
      if (Math.abs(brightness(colour) - brightness(back)) >= 90) continue;
      e.style.setProperty('color', brightness(back) > 140 ? DARK_TEXT : LIGHT_TEXT, 'important');
    }

    goldFormStars();
    enforceInputSize();
    syncKeyboardFlag();
  }

  // requestAnimationFrame, not setTimeout: rAF fires BEFORE the next paint, so
  // a freshly-rendered white panel gets darkened in the same frame it appears.
  // With a 120ms timeout the browser painted the white panel first — that was
  // the white flash when switching photos in the lightbox.
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    var raf = window.requestAnimationFrame || function (f) { return setTimeout(f, 16); };
    raf(function () { queued = false; run(); });
  }

  function start() {
    run();

    // Poll only while the page is settling — Judge.me renders its widgets late
    // and re-asserts its own colours just after load. Once that's done the
    // observer covers everything (pagination, sort, lightbox, form open/cancel).
    var ticks = 0;
    var iv = setInterval(function () {
      run();
      if (++ticks >= 24) clearInterval(iv); // ~17s
    }, 700);

    try {
      var obs = new MutationObserver(schedule);
      if (document.body) {
        obs.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    } catch (e) {}

    // Keyboard flag is event-driven — no polling, and it reacts the instant a
    // field is focused or blurred.
    document.addEventListener('focusin', syncKeyboardFlag, true);
    document.addEventListener('focusout', function () {
      // Let the browser move focus first, otherwise activeElement is still the
      // field we just left.
      setTimeout(syncKeyboardFlag, 0);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
